import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import AttendanceHistoryPage from './pages/AttendanceHistoryPage';
import CompanyRegisterPage from './pages/CompanyRegisterPage';
import UserManagementPage from './pages/UserManagementPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import CorrectionRequestsPage from './pages/CorrectionRequestsPage';
import OrganizationManagementPage from './pages/OrganizationManagementPage';
import AttendanceExportPage from './pages/AttendanceExportPage';
import CompanySettingsPage from './pages/CompanySettingsPage';
import LeaveRequestsPage from './pages/LeaveRequestsPage';
import LeaveTypesPage from './pages/LeaveTypesPage';
import LeaveBalancesPage from './pages/LeaveBalancesPage';
import KioskPage from './pages/KioskPage';
import KioskSetupPage from './pages/KioskSetupPage';
import KioskDevicesPage from './pages/KioskDevicesPage';
import MyShiftPage from './pages/MyShiftPage';
import ShiftComparisonPage from './pages/ShiftComparisonPage';
import ShiftBuildPage from './pages/ShiftBuildPage';
import ShiftRequestAdminPage from './pages/ShiftRequestAdminPage';
import MyShiftRequestPage from './pages/MyShiftRequestPage';
import MyPunchQrPage from './pages/MyPunchQrPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import VerifyEmailChangePage from './pages/VerifyEmailChangePage';
import ProfilePage from './pages/ProfilePage';
import SubscriptionPage from './pages/SubscriptionPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import LegalPage from './pages/LegalPage';
import ContactPage from './pages/ContactPage';
import { trackPageView } from './lib/analytics';


/**
 * 画面ごとに document.title を切り替える。
 *
 * index.html の <title> は LP の SEO 用に長い文言（「キンログ - シンプルで使いやすい
 * クラウド勤怠管理システム | 月額1,000円から」）になっている。デスクトップに PWA として
 * インストールすると、ウィンドウのタイトルバーは **manifest の名前 + document.title** を
 * 連結して表示するため、この長文がそのまま出てしまう。
 *
 * アプリ内の画面では短いタイトルに差し替え、LP など公開ページでは SEO 用の元タイトルを維持する。
 */
const SEO_TITLE = document.title;

const PAGE_TITLES: Record<string, string> = {
  '/dashboard': 'ダッシュボード',
  '/attendance-history': '勤怠履歴',
  '/users': 'ユーザー管理',
  '/correction-requests': '修正申請',
  '/organization': '組織管理',
  '/attendance-export': '勤怠エクスポート',
  '/company-settings': '企業設定',
  '/subscription': 'サブスクリプション',
  '/leave-requests': '休暇申請',
  '/leave-balances': '休暇残数',
  '/leave-types': '休暇種別',
  '/profile': 'プロフィール',
  '/change-password': 'パスワード変更',
  '/contact': 'お問い合わせ',
  '/login': 'ログイン',
  '/register': 'ユーザー登録',
  '/company-register': '事業者登録',
  '/forgot-password': 'パスワード再設定',
  '/reset-password': 'パスワード再設定',
  // 共用端末。店頭のタブレットはタブ名が見えることがあるので、何の画面か分かる名前にする
  '/kiosk': '打刻',
  '/kiosk/setup': '打刻用共用端末の登録',
  '/kiosk-devices': '打刻用共用端末',
  '/my-punch-qr': '打刻用QRコード',
  '/shifts': 'シフトを組む',
  '/shift-comparison': '予定と実績',
  '/my-shift': '自分のシフト',
  '/shift-requests': '希望シフトの受付',
  '/my-shift-request': '希望シフトの提出',
};


/**
 * 画面遷移したら先頭へスクロールする。
 *
 * React Router は遷移してもスクロール位置を維持するため、LP のフッターにある
 * 「特定商取引法に基づく表記」等を押すと、**遷移先が下までスクロールした状態**で開き、
 * 「途中から始まる」ように見えていた。
 *
 * ただしハッシュ付き（LP 内の #faq などのアンカー）は対象外。
 * これを一律に先頭へ戻すと、アンカーリンクが機能しなくなる。
 */
const ScrollToTop: React.FC = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

const DocumentTitle: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const page = PAGE_TITLES[pathname];
    // LP・規約など公開ページは SEO 用の元タイトルのまま
    document.title = page ? `${page} | キンログ` : SEO_TITLE;
  }, [pathname]);
  return null;
};


/**
 * 画面遷移を GA4 に送る。
 *
 * SPA は最初の1回しかドキュメントが読まれないため、ルート遷移を手動で計測しないと
 * 「LP しか見られていない」ように見える。DocumentTitle より後ろに置き、
 * 差し替わった後のタイトルで送る。
 *
 * 送るのは **パスのみ**（クエリは analytics 側で落とす）。
 * `/reset-password?token=...` のトークンを解析サービスへ渡さないため。
 */
const PageViewTracker: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    trackPageView(pathname);
  }, [pathname]);
  return null;
};

const AppContent: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <>
    <DocumentTitle />
    <PageViewTracker />
    <ScrollToTop />
    <Routes>
      {/* パブリックルート */}
      <Route
        path="/"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />}
      />
      <Route
        path="/terms"
        element={<TermsPage />}
      />
      <Route
        path="/privacy"
        element={<PrivacyPage />}
      />
      <Route
        path="/contact"
        element={<ContactPage />}
      />
      <Route
        path="/legal"
        element={<LegalPage />}
      />
      {/* /beta は廃止。LP へリダイレクト */}
      <Route
        path="/beta"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />}
      />
      <Route
        path="/register"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <RegisterPage />}
      />
      <Route
        path="/company-register"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <CompanyRegisterPage />}
      />
      <Route
        path="/forgot-password"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <ForgotPasswordPage />}
      />
      <Route
        path="/reset-password"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <ResetPasswordPage />}
      />
      <Route
        path="/verify-email"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <VerifyEmailPage />}
      />
      <Route
        path="/verify-email-change"
        element={<VerifyEmailChangePage />}
      />

      {/* プライベートルート */}
      <Route
        path="/change-password"
        element={
          <PrivateRoute>
            <ChangePasswordPage />
          </PrivateRoute>
        }
      />
      {/*
        共用端末(キオスク)の2画面は **PrivateRoute に入れない**。
        認証は端末トークン Cookie(層1)で行うため、ログインを要求すると
        「店に置く端末に管理者アカウントでログインさせる」ことになり、
        この機能が避けようとしている状態そのものになる。
        ログイン済みかどうかで挙動を変えないのも意図的（店の端末で
        たまたま誰かがログインしていても、打刻画面は打刻画面のまま）。
      */}
      <Route path="/kiosk" element={<KioskPage />} />
      <Route path="/kiosk/setup" element={<KioskSetupPage />} />

      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <DashboardPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/my-punch-qr"
        element={
          <PrivateRoute>
            <MyPunchQrPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/kiosk-devices"
        element={
          <PrivateRoute>
            <KioskDevicesPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/shifts"
        element={
          <PrivateRoute>
            <ShiftBuildPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/shift-comparison"
        element={
          <PrivateRoute>
            <ShiftComparisonPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/my-shift"
        element={
          <PrivateRoute>
            <MyShiftPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/shift-requests"
        element={
          <PrivateRoute>
            <ShiftRequestAdminPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/my-shift-request"
        element={
          <PrivateRoute>
            <MyShiftRequestPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/attendance-history"
        element={
          <PrivateRoute>
            <AttendanceHistoryPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/users"
        element={
          <PrivateRoute>
            <UserManagementPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/correction-requests"
        element={
          <PrivateRoute>
            <CorrectionRequestsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/organization"
        element={
          <PrivateRoute>
            <OrganizationManagementPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/attendance-export"
        element={
          <PrivateRoute>
            <AttendanceExportPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/company-settings"
        element={
          <PrivateRoute>
            <CompanySettingsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/leave-requests"
        element={
          <PrivateRoute>
            <LeaveRequestsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/leave-types"
        element={
          <PrivateRoute>
            <LeaveTypesPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/leave-balances"
        element={
          <PrivateRoute>
            <LeaveBalancesPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <PrivateRoute>
            <ProfilePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <PrivateRoute>
            <SubscriptionPage />
          </PrivateRoute>
        }
      />

      {/* 404 */}
      <Route
        path="*"
        element={<Navigate to="/" replace />}
      />
    </Routes>
    </>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <div style={{ minHeight: '100vh', backgroundColor: '#f5f9fd' }}>
          <AppContent />
        </div>
      </AuthProvider>
    </Router>
  );
};

export default App;
